import { ToastVariant } from "@enxcs/dap-ui-stratos";
import { FC, MouseEventHandler, ReactNode, useState } from "react";
import Toast from "../../components/Toast/Toast";
import { useTranslation } from "react-i18next";
import { ToastAction, ToastContextValue, ToastContext } from "./ToastContext";

export class ToastState {
  open = false;
  text = "";
  variant = ToastVariant.SUCCESS;
  linkLabel?: string;
  autoClose?: boolean;
  onLinkClick?: MouseEventHandler<HTMLButtonElement>;
}

export interface ToastProviderProps {
  children: ReactNode | ReactNode[];
  closeTitle?: string;
}

const ToastProvider: FC<ToastProviderProps> = ({ children, closeTitle = "buttons.close" }) => {
  const [state, setState] = useState(new ToastState());

  const { t, i18n } = useTranslation();

  const showProgressToast: ToastAction = (text, options = {}) => {
    const { linkLabel, onLinkClick, autoClose = false } = options;

    setState({
      open: true,
      text,
      variant: ToastVariant.PROGRESS,
      linkLabel,
      onLinkClick,
      autoClose,
    });
  };

  const showSuccessToast: ToastAction = (text, options = {}) => {
    const { linkLabel, onLinkClick, autoClose = true } = options;

    setState({
      open: true,
      text,
      variant: ToastVariant.SUCCESS,
      linkLabel,
      onLinkClick,
      autoClose,
    });
  };

  const showErrorToast: ToastAction = (text, options = {}) => {
    const { linkLabel, onLinkClick, autoClose = true } = options;

    setState({
      open: true,
      text,
      variant: ToastVariant.ERROR,
      linkLabel,
      onLinkClick,
      autoClose,
    });
  };

  const hideToast = () => setState(new ToastState());

  const contextValue: ToastContextValue = {
    showProgressToast,
    showSuccessToast,
    showErrorToast,
    hideToast,
  };

  const resolveText = (value: string) => {
    const hasKey =
      i18n.exists(value) || // key exists normally
      i18n.exists(value, { fallbackLng: false }); // avoid fallback behaving weirdly

    return hasKey ? t(value) : value;
  };

  return (
    <ToastContext value={contextValue}>
      {children}

      {state.open && (
        <Toast
          text={resolveText(state.text)}
          variant={state.variant}
          linkLabel={state.linkLabel}
          closeTitle={resolveText(closeTitle)}
          onLinkClick={state.onLinkClick}
          onClose={hideToast}
          autoClose={state.autoClose}
        />
      )}
    </ToastContext>
  );
};

export default ToastProvider;
